/**
 * Shared building-blocks for the read-only PitchBook preview dialogs.
 *
 * The dialogs compare indexed PitchBook data with the CRM; they never write.
 * PitchBook records reach the CRM only through List Management (#767), so the
 * owner picker, include/select checkboxes and per-field decisions that fed the
 * removed commit are gone.
 *
 * Exports:
 *  - DIFF_STATES        — constants
 *  - pbValue, diffState, prettyField  — helpers
 *  - DiffTable          — per-field side-by-side diff table (saved mapping policy)
 *  - VerdictBadge       — how the record lines up with the CRM
 *  - PersonRow          — collapsible person row + DiffTable
 *  - MovedToListManagement — where importing happens now (#767)
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { isEmpty, valuesEqual, fmtValue } from '../../utils/pitchbookDiff';

// ─── Constants ────────────────────────────────────────────────────────────────

export const DIFF_STATES = {
    conflict: { label: 'Differs',   cls: 'bg-amber-50 text-amber-700 border-amber-200',      order: 0 },
    fill:     { label: 'Would fill', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', order: 1 },
    match:    { label: 'Match',     cls: 'bg-stone-100 text-stone-500 border-stone-200',      order: 2 },
    empty:    { label: 'No data',   cls: 'bg-stone-50 text-stone-400 border-stone-200',       order: 3 },
};

const WRITE_POLICIES = ['fill_blank', 'always', 'ignore'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** The value as it would actually be written to the CRM (backend-converted). */
export function pbValue(row) {
    return row.crm_value !== undefined && row.crm_value !== null
        ? row.crm_value : row.coerced_value;
}

export function diffState(row) {
    if (isEmpty(pbValue(row))) return 'empty';
    if (isEmpty(row.crm_current)) return 'fill';
    if (valuesEqual(pbValue(row), row.crm_current, row.target)) return 'match';
    return 'conflict';
}

/** The saved mapping's write policy for a row. */
function mappingPolicy(row) {
    return WRITE_POLICIES.includes(row.policy) ? row.policy : 'ignore';
}

export function prettyField(f) {
    if (!f) return '';
    return f.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()).replace(/\bUrl\b/, 'URL');
}

/** Whether the saved PitchBook mapping's policy would write this row (arrow highlight only). */
function wouldWrite(row) {
    const policy = mappingPolicy(row);
    if (isEmpty(pbValue(row))) return false;
    if (policy === 'always') return true;
    if (policy === 'fill_blank') return isEmpty(row.crm_current);
    return false;
}

// ─── VerdictBadge ─────────────────────────────────────────────────────────────

export function VerdictBadge({ verdict }) {
    if (!verdict || verdict === 'skip_no_change') {
        return (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium border bg-stone-50 text-stone-500 border-stone-200 shrink-0">
                No change
            </span>
        );
    }
    if (verdict === 'ambiguous') {
        return (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium border bg-amber-50 text-amber-700 border-amber-200 shrink-0">
                Ambiguous match
            </span>
        );
    }
    const isCreate = verdict === 'create';
    return (
        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border shrink-0 ${
            isCreate
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
            {isCreate ? 'Not in CRM' : 'Differs from CRM'}
        </span>
    );
}

// ─── DiffTable ────────────────────────────────────────────────────────────────

export function DiffTable({ fields, isUpdate }) {
    const [showUnmapped, setShowUnmapped] = useState(false);

    const { mapped, unmapped } = useMemo(() => {
        const m = [];
        const u = [];
        for (const f of fields) {
            if (!f.target || f.policy === 'pitchbook_tab_only') u.push(f);
            else m.push(f);
        }
        m.sort((a, b) => DIFF_STATES[diffState(a)].order - DIFF_STATES[diffState(b)].order
            || a.csv_column.localeCompare(b.csv_column));
        return { mapped: m, unmapped: u };
    }, [fields]);

    return (
        <div className="border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs table-fixed">
                <thead className="bg-stone-50 text-stone-500">
                    <tr>
                        <th className="text-left px-3 py-2 font-medium w-[26%]">Field</th>
                        <th className="text-left px-3 py-2 font-medium w-[30%]">PitchBook value</th>
                        <th className="w-6" />
                        <th className="text-left px-3 py-2 font-medium w-[30%]">CRM current</th>
                        <th className="text-left px-3 py-2 font-medium w-[14%]">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                    {mapped.map((f) => {
                        const state = diffState(f);
                        const meta = DIFF_STATES[state];
                        const pb = fmtValue(pbValue(f));
                        const crm = fmtValue(f.crm_current);
                        const writes = wouldWrite(f);
                        return (
                            <tr key={f.csv_column} className={`hover:bg-stone-50 ${state === 'empty' ? 'opacity-60' : ''}`}>
                                <td className="px-3 py-1.5 align-top">
                                    <span className="text-stone-700 font-medium block truncate" title={f.target}>{prettyField(f.target)}</span>
                                    <code className="text-[10px] text-stone-400 block truncate" title={f.csv_column}>{f.csv_column}</code>
                                </td>
                                <td className={`px-3 py-1.5 align-top break-words ${writes ? 'text-stone-800' : 'text-stone-500'}`}
                                    title={f.raw_value && f.raw_value !== pb ? `PitchBook: ${f.raw_value}` : undefined}>
                                    {pb ?? <span className="text-stone-300">—</span>}
                                </td>
                                <td className="text-center align-top py-1.5">
                                    <ArrowRight className={`w-3 h-3 inline ${writes ? 'text-emerald-500' : 'text-stone-200'}`} />
                                </td>
                                <td className="px-3 py-1.5 align-top break-words text-stone-500">
                                    {crm ?? <span className="text-stone-300">{isUpdate ? '—' : 'new record'}</span>}
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-medium whitespace-nowrap ${meta.cls}`}>
                                        {meta.label}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {unmapped.length > 0 && (
                <button
                    onClick={() => setShowUnmapped((v) => !v)}
                    className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] text-stone-400 hover:text-stone-600 bg-stone-50/50 border-t border-stone-100"
                >
                    {showUnmapped ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {unmapped.length} column{unmapped.length === 1 ? '' : 's'} not mapped to the CRM (kept on the PitchBook tab only)
                </button>
            )}
            {showUnmapped && (
                <div className="px-3 py-2 border-t border-stone-100 grid grid-cols-2 gap-x-6 gap-y-0.5">
                    {unmapped.map((f) => (
                        <div key={f.csv_column} className="flex items-baseline gap-2 text-[11px] min-w-0">
                            <code className="text-stone-500 shrink-0">{f.csv_column}</code>
                            <span className="text-stone-400 truncate">{f.raw_value || '—'}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── PersonRow ────────────────────────────────────────────────────────────────

export function PersonRow({ person, expanded, onToggleExpand, highlighted }) {
    return (
        <div className={`border rounded-lg ${highlighted ? 'border-stone-400 ring-1 ring-stone-200' : 'border-stone-200'}`}>
            <div className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-stone-50">
                <button type="button"
                    onClick={onToggleExpand}
                    className="flex items-center gap-1.5 min-w-0 text-sm text-stone-700 truncate text-left">
                    {expanded
                        ? <ChevronDown className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                        : <ChevronRight className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
                    <span className="truncate">{person.name || person.pitchbook_id}</span>
                </button>
                <VerdictBadge verdict={person.verdict} />
            </div>
            {expanded && (
                <div className="p-2 border-t border-stone-100">
                    <DiffTable fields={person.fields} isUpdate={person.verdict !== 'create'} />
                </div>
            )}
        </div>
    );
}

/**
 * PitchBook no longer writes CRM records from these dialogs (#767, #777).
 * Records reach the CRM through List Management's reviewed, Admin-confirmed
 * commit, and the PitchBook source there needs licensing approval.
 */
export function MovedToListManagement({ compact = false }) {
    return (
        <div data-testid="pb-moved-to-list-management"
             className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'} text-stone-600`}>
            <span>Importing into the CRM now happens in</span>
            <Link to="/list-management" className="font-medium text-indigo-700 hover:underline">
                CRM → Insights → List Management
            </Link>
            <span className="text-stone-400">(PitchBook source requires licensing approval)</span>
        </div>
    );
}

